import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bot, Wrench, ChevronDown, CheckSquare, Square, Copy,
  Home, Globe, Workflow, Search, Loader2, Server, Play, Pause, Star,
  Sparkles, Brain, Zap, Shield, User, Phone, Volume2, RefreshCw
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@easyflow/ui-frontend/components/ui/card';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent
} from '@easyflow/ui-frontend/components/ui/accordion';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@easyflow/ui-frontend/components/ui/tooltip';
import { paConfigurationCatalogResponseSchema } from '@easyflow/shared';
import { apiClient as api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { emitPersonalAssistantUpdated } from '@/lib/personal-assistant-events';
import { getErrorMessage } from '../../lib/utils';
import { AgentToolPermissionsEditor, withPaOperationMode } from './AgentToolPermissionsEditor';
import type {
  McpAgentPermission as AgentPermission,
  McpAgentWorkflowBinding as WorkflowBinding,
  McpServerListItem as MCPServer,
  McpServerToolListItem,
  McpBatchUpdateResponse,
  McpCopyBindingsResponse,
  McpRefreshAgentToolsResponse,
  ElevenLabsSyncToolsData,
} from '@easyflow/shared';

// Tool aggregato dal pannello: la riga condivisa + il server di appartenenza
// (sempre valorizzato dall'aggregazione per-server qui sotto).
type MCPTool = McpServerToolListItem & { mcp_server_id: string };

interface AIAgent {
  id: string;
  name: string;
  description?: string;
  type: 'voice' | 'chat' | 'personal' | 'elevenlabs_sip' | 'elevenlabs_webrtc';
  is_active: boolean;
  avatar_icon?: string;
  color_theme?: string;
  assigned_user_name?: string;
}


// Shape parziali dei payload wire mappati verso AIAgent (solo campi letti).
interface VoiceAgentWire {
  id: string;
  name: string;
  description?: string;
  is_active?: boolean;
  template_code?: string;
  [key: string]: unknown;
}

interface PersonalAssistantWire {
  ai_agent_id: string;
  name?: string;
  display_name?: string;
  is_active?: boolean;
  avatar_icon?: string;
  color_theme?: string;
  assigned_user_first_name?: string;
  assigned_user_last_name?: string;
  assigned_user_email?: string;
}

interface ElevenLabsAgentWire {
  id: string;
  name: string;
  description?: string;
  is_active?: boolean;
  channel_sip?: boolean;
  channel_webrtc?: boolean;
}



interface WorkflowSummary {
  id: string;
  name: string;
  description?: string;
  trigger_type?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

interface AgentBindingsPanelProps {
  agents?: AIAgent[];
  internalTools?: MCPTool[];
  externalTools?: MCPTool[];
  workflows?: WorkflowSummary[];
}

export function AgentBindingsPanel({ 
  agents: agentsProp, 
  internalTools: internalToolsProp, 
  externalTools: externalToolsProp,
  workflows: workflowsProp 
}: AgentBindingsPanelProps = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isInternalServer = (s: MCPServer) => s.name === 'EASYFlow' || s.name === 'EDG VoIP Tools' || s.id === '00000000-0000-0000-0000-000000000001';

  const { data: selfAgents } = useQuery({
    queryKey: ['ai-agents-all-bindings'],
    queryFn: async () => {
      const voiceRes = await api.get('/ai/voice-agents').catch(() => ({ data: [] as VoiceAgentWire[] }));
      const voiceRaw = voiceRes.data as VoiceAgentWire[] | { data?: VoiceAgentWire[] } | undefined;
      const voiceData: VoiceAgentWire[] = Array.isArray(voiceRaw) ? voiceRaw : (voiceRaw?.data || []);
      const voiceAgents = voiceData.map((a) => ({
        ...a,
        type: (a.template_code === 'receptionist' || a.template_code === 'support' || a.template_code === 'sales') ? 'voice' as const : 'chat' as const
      }));

      const paRes = await api.get('/personal-assistant/agents').catch(() => ({ data: [] as PersonalAssistantWire[] }));
      const paRaw = paRes.data as PersonalAssistantWire[] | { data?: PersonalAssistantWire[] } | undefined;
      const paData: PersonalAssistantWire[] = Array.isArray(paRaw) ? paRaw : (paRaw?.data || []);
      const personalAgents = paData.map((pa) => {
        const userName = pa.assigned_user_first_name
          ? `${pa.assigned_user_first_name} ${pa.assigned_user_last_name || ''}`.trim()
          : pa.assigned_user_email || null;
        return {
          id: pa.ai_agent_id,
          name: pa.display_name || pa.name,
          description: userName ? `Assistente di ${userName}` : 'Assistente Personale AI',
          is_active: pa.is_active,
          type: 'personal' as const,
          avatar_icon: pa.avatar_icon,
          color_theme: pa.color_theme,
          assigned_user_name: userName
        };
      });

      const elRes = await api.get('/elevenlabs/agents').catch(() => ({ data: [] as ElevenLabsAgentWire[] }));
      const elRaw: unknown = elRes?.data || elRes;
      const elData: ElevenLabsAgentWire[] = Array.isArray(elRaw)
        ? elRaw
        : ((elRaw as { data?: ElevenLabsAgentWire[] })?.data || []);
      const elevenlabsAgents = elData.map((a) => {
        const hasSip = a.channel_sip !== false;
        const hasWebrtc = a.channel_webrtc !== false;
        const channels = [hasSip && 'SIP', hasWebrtc && 'WebRTC'].filter(Boolean).join(' + ');
        const type = hasSip ? 'elevenlabs_sip' : 'elevenlabs_webrtc';
        return {
          id: a.id,
          name: `${a.name} (${channels})`,
          description: a.description || `ElevenLabs Voice Agent - ${channels}`,
          is_active: a.is_active ?? true,
          type: type,
        };
      });

      return [...voiceAgents, ...personalAgents, ...elevenlabsAgents] as AIAgent[];
    },
    enabled: !agentsProp
  });

  const { data: serversData, error: serversError } = useQuery({
    queryKey: ['mcp-servers-bindings'],
    queryFn: async () => {
      const res = await api.get<MCPServer[] | { data?: MCPServer[] }>('/mcp/servers');
      const responseData = res.data;
      if (Array.isArray(responseData)) return responseData as MCPServer[];
      return (responseData)?.data || [];
    },
    enabled: !internalToolsProp || !externalToolsProp
  });

  const needToolsFetch = !internalToolsProp || !externalToolsProp;

  const enabledServers = useMemo(() => {
    if (!needToolsFetch) return [];
    const servers = serversData || [];
    const internal = servers.find((s: MCPServer) => isInternalServer(s));
    const enabled = servers.filter((s: MCPServer) => s.enabled);
    if (internal && !enabled.find((s: MCPServer) => s.id === internal.id)) {
      return [internal, ...enabled];
    }
    return enabled;
  }, [serversData, needToolsFetch]);

  const { data: selfToolsData, error: toolsError, isLoading: toolsLoading } = useQuery({
    queryKey: ['mcp-all-tools-bindings', enabledServers.map((s: MCPServer) => s.id).join(',')],
    queryFn: async () => {
      if (enabledServers.length === 0) return [];
      const toolsPromises = enabledServers.map((s: MCPServer) =>
        api.get<McpServerToolListItem[] | { data?: McpServerToolListItem[] }>(`/mcp/servers/${s.id}/tools`)
      );
      const results = await Promise.all(toolsPromises);
      const allTools: MCPTool[] = [];
      results.forEach((result, idx) => {
        const responseData = result.data;
        const tools = Array.isArray(responseData) ? responseData : (responseData?.data || []);
        const server = enabledServers[idx]!;
        tools.forEach((t: McpServerToolListItem) => {
          if (t.enabled !== false) {
            allTools.push({
              ...t,
              mcp_server_id: server.id,
              server_name: server.name,
              is_internal: isInternalServer(server)
            });
          }
        });
      });
      return allTools;
    },
    enabled: needToolsFetch && enabledServers.length > 0
  });

  const { data: selfWorkflows } = useQuery({
    queryKey: ['workflows-bindings'],
    queryFn: async () => {
      const response = await api.get('/workflows') as { workflows?: WorkflowSummary[]; data?: { workflows?: WorkflowSummary[] } };
      const wfs: WorkflowSummary[] = response.workflows || response.data?.workflows || [];
      return wfs;
    },
    enabled: !workflowsProp
  });

  const agents = agentsProp || selfAgents || [];
  const internalTools = internalToolsProp || (selfToolsData || []).filter((t: MCPTool) => t.is_internal);
  const externalTools = externalToolsProp || (selfToolsData || []).filter((t: MCPTool) => !t.is_internal);
  const workflows = workflowsProp || selfWorkflows || [];
  const { data: paCatalog, isLoading: paCatalogLoading, error: paCatalogError } = useQuery({
    queryKey: ['pa-configuration-bindings-catalog'],
    enabled: agents.some(agent => agent.type === 'personal'),
    queryFn: async () => {
      const response = await api.get<unknown>('/personal-assistant/configuration-catalog');
      return paConfigurationCatalogResponseSchema.parse(response.data);
    },
  });
  
  const [expandedAgents, setExpandedAgents] = useState<string[]>([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState<Record<string, Set<string>>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceAgent, setCopySourceAgent] = useState<string | null>(null);
  const [copyTargetAgent, setCopyTargetAgent] = useState<string | null>(null);
  const [copyWorkflowsDialogOpen, setCopyWorkflowsDialogOpen] = useState(false);
  const [copyWorkflowsSourceAgent, setCopyWorkflowsSourceAgent] = useState<string | null>(null);
  const [copyWorkflowsTargetAgent, setCopyWorkflowsTargetAgent] = useState<string | null>(null);

  const [refreshingAgent, setRefreshingAgent] = useState<string | null>(null);

  const refreshAgentTools = async (agentId: string) => {
    setRefreshingAgent(agentId);
    try {
      const result = await api.postTopLevel<McpRefreshAgentToolsResponse>(`/mcp/agent-permissions/${agentId}/refresh-tools`);
      const agent = agents.find(a => a.id === agentId);
      const isElevenlabs = agent?.type === 'elevenlabs_sip' || agent?.type === 'elevenlabs_webrtc';
      const elSyncAction = (result.actions || []).find((a) => a.startsWith('elevenlabs_synced'));
      const elSynced = elSyncAction ? elSyncAction.split(':')[1] : null;

      let desc = `${result.tools_count} tools attivi`;
      if (isElevenlabs && elSynced) desc += ` — ${elSynced} sincronizzati con ElevenLabs`;
      if (result.agent_type === 'personal') desc += ' — contesto PA invalidato';

      toast({ title: 'Tools aggiornati', description: desc });
      queryClient.invalidateQueries({ queryKey: ['agent-permissions'] });
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' });
    } finally {
      setRefreshingAgent(null);
    }
  };

  const syncElevenLabsTools = async (agentId: string) => {
    const agent = agents.find(a => a.id === agentId);
    if (!agent || (agent.type !== 'elevenlabs_sip' && agent.type !== 'elevenlabs_webrtc')) return;
    try {
      const result = await api.post<ElevenLabsSyncToolsData>(`/elevenlabs/agents/${agentId}/sync-tools`, {});
      toast({ title: 'ElevenLabs Sync', description: `${result.data?.synced || 0} tools sincronizzati con ElevenLabs` });
    } catch (e) {
      console.warn('[ElevenLabs] Auto-sync tools failed:', getErrorMessage(e));
    }
  };

  const { data: permissionsData, isLoading: permissionsLoading, error: permissionsError } = useQuery({
    queryKey: ['agent-permissions'],
    queryFn: async () => {
      const data = await api.get<AgentPermission[]>('/mcp/agent-permissions');
      return data.data || [];
    }
  });

  const { data: workflowBindingsData, isLoading: workflowBindingsLoading } = useQuery({
    queryKey: ['agent-workflows'],
    queryFn: async () => {
      const data = await api.get<WorkflowBinding[]>('/mcp/agent-workflows');
      return data.data || [];
    }
  });

  const agentPermissions = useMemo(() => {
    const perms: Record<string, AgentPermission[]> = {};
    (permissionsData || []).forEach((p) => {
      if (!perms[p.agent_id]) perms[p.agent_id] = [];
      perms[p.agent_id]!.push(p)!;
    });
    return perms;
  }, [permissionsData]);

  const agentWorkflowBindings = useMemo(() => {
    const bindings: Record<string, WorkflowBinding[]> = {};
    (workflowBindingsData || []).forEach((b) => {
      if (!bindings[b.agent_id]) bindings[b.agent_id] = [];
      bindings[b.agent_id]!.push(b)!;
    });
    return bindings;
  }, [workflowBindingsData]);

  const updatePermissionMutation = useMutation({
    mutationFn: async (data: { agent_id: string; tool_name: string; tool_type: string; server_id?: string; enabled: boolean; bulk_allowed?: boolean }) => {
      return api.put('/mcp/agent-permissions', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-permissions'] });
      emitPersonalAssistantUpdated({ assistantId: null });
    },
    onError: (error: unknown) => {
      toast({ title: 'Limite raggiunto', description: getErrorMessage(error), variant: 'destructive' });
    }
  });

  const batchPermissionsMutation = useMutation({
    mutationFn: async (data: { agent_id: string; tools: { tool_name: string; server_id?: string }[]; enabled: boolean }) => {
      const result = await api.putTopLevel<McpBatchUpdateResponse>('/mcp/agent-permissions/batch', data);
      return { ...result, _agentId: data.agent_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agent-permissions'] });
      emitPersonalAssistantUpdated({ assistantId: null });
      toast({ title: 'Permessi aggiornati', description: `${data.updated} tools aggiornati` });
      if (data._agentId) syncElevenLabsTools(data._agentId);
    },
    onError: (error: unknown) => {
      toast({ title: 'Limite raggiunto', description: getErrorMessage(error), variant: 'destructive' });
    }
  });

  const batchByFilterMutation = useMutation({
    // filter_type 'tags' accetta filter_value string | string[] (tag servizio
    // gmail/drive/docs/sheets/calendar/contacts/meet e/o capacità read/write).
    mutationFn: async (data: { agent_id: string; filter_type: 'server' | 'category' | 'all' | 'tags'; filter_value?: string | string[]; enabled: boolean }) => {
      const result = await api.putTopLevel<McpBatchUpdateResponse>('/mcp/agent-permissions/batch-by-filter', data);
      return { ...result, _agentId: data.agent_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agent-permissions'] });
      emitPersonalAssistantUpdated({ assistantId: null });
      toast({ title: 'Permessi aggiornati', description: `${data.updated} tools aggiornati` });
      if (data._agentId) syncElevenLabsTools(data._agentId);
    },
    onError: (error: unknown) => {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    },
  });

  const copyPermissionsMutation = useMutation({
    mutationFn: async (data: { source_agent_id: string; target_agent_id: string; overwrite: boolean }) => {
      return api.postTopLevel<McpCopyBindingsResponse>('/mcp/agent-permissions/copy', data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agent-permissions'] });
      emitPersonalAssistantUpdated({ assistantId: null });
      toast({ title: 'Configurazione copiata', description: `${data.copied} permessi copiati` });
      setCopyDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    },
  });

  const updateWorkflowBindingMutation = useMutation({
    mutationFn: async (data: { agent_id: string; workflow_id: string; enabled: boolean }) => {
      return api.put('/mcp/agent-workflows', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-workflows'] });
    }
  });

  const batchWorkflowsMutation = useMutation({
    mutationFn: async (data: { agent_id: string; workflow_ids: string[]; enabled: boolean }) => {
      return api.putTopLevel<McpBatchUpdateResponse>('/mcp/agent-workflows/batch', data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agent-workflows'] });
      toast({ title: 'Workflows aggiornati', description: `${data.updated} workflows aggiornati` });
    }
  });

  const copyWorkflowsMutation = useMutation({
    mutationFn: async (data: { source_agent_id: string; target_agent_id: string; overwrite: boolean }) => {
      return api.postTopLevel<McpCopyBindingsResponse>('/mcp/agent-workflows/copy', data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agent-workflows'] });
      toast({ title: 'Workflows copiati', description: `${data.copied} bindings copiati` });
      setCopyWorkflowsDialogOpen(false);
    }
  });

  const isWorkflowEnabledForAgent = (agentId: string, workflowId: string) => {
    const bindings = agentWorkflowBindings[agentId] || [];
    return bindings.some(b => b.workflow_id === workflowId && b.enabled);
  };

  const MAX_TOOLS_PER_AGENT = 128;

  const getAgentToolCounts = (agentId: string) => {
    const perms = agentPermissions[agentId] || [];
    const enabledTools = perms.filter(p => p.enabled).length;
    const totalTools = internalTools.length + externalTools.length;
    return { enabled: enabledTools, total: totalTools, max: MAX_TOOLS_PER_AGENT };
  };

  const getAgentWorkflowCounts = (agentId: string) => {
    const bindings = agentWorkflowBindings[agentId] || [];
    const enabledWorkflows = bindings.filter(b => b.enabled).length;
    return { enabled: enabledWorkflows, total: workflows.length };
  };

  const toggleWorkflowSelection = (agentId: string, workflowId: string) => {
    setSelectedWorkflows(prev => {
      const agentSet = new Set(prev[agentId] || []);
      if (agentSet.has(workflowId)) {
        agentSet.delete(workflowId);
      } else {
        agentSet.add(workflowId);
      }
      return { ...prev, [agentId]: agentSet };
    });
  };

  const handleBatchByFilter = (agentId: string, filterType: 'server' | 'category' | 'all' | 'tags', filterValue: string | string[] | undefined, enabled: boolean) => {
    batchByFilterMutation.mutate({ agent_id: agentId, filter_type: filterType, filter_value: filterValue, enabled });
  };

  const handleBatchWorkflows = (agentId: string, enabled: boolean) => {
    const selected = selectedWorkflows[agentId];
    if (!selected || selected.size === 0) return;

    batchWorkflowsMutation.mutate({ 
      agent_id: agentId, 
      workflow_ids: Array.from(selected), 
      enabled 
    });
    setSelectedWorkflows(prev => ({ ...prev, [agentId]: new Set() }));
  };

  const externalToolsByServer = useMemo(() => {
    const byServer: Record<string, { serverName: string; tools: MCPTool[] }> = {};
    externalTools.forEach(tool => {
      const serverId = tool.mcp_server_id;
      if (!byServer[serverId]) {
        byServer[serverId] = { serverName: tool.server_name || serverId, tools: [] };
      }
      byServer[serverId].tools.push(tool);
    });
    return byServer;
  }, [externalTools]);

  const filterWorkflows = (wfs: WorkflowSummary[]) => {
    if (!searchQuery) return wfs;
    const q = searchQuery.toLowerCase();
    return wfs.filter(w => 
      w.name.toLowerCase().includes(q) || 
      w.description?.toLowerCase().includes(q)
    );
  };

  if (agents.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-lg font-medium mb-2">Nessun AI Agent configurato</h3>
          <p className="text-muted-foreground">
            Crea prima un AI Agent dalla sezione AI Tools.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {(serversError || toolsError || permissionsError || paCatalogError) && <p role="alert" className="text-sm text-destructive">{getErrorMessage(serversError || toolsError || permissionsError || paCatalogError)}</p>}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-semibold">Permessi Tools e Workflows per AI Agents</h3>
          <p className="text-sm text-muted-foreground">
            Configura quali tools e workflows ogni agente può utilizzare.
          </p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca tools o workflows..."
            className="pl-9"
          />
        </div>
      </div>

      <Accordion 
        type="multiple" 
        value={expandedAgents}
        onValueChange={setExpandedAgents}
        className="space-y-4"
      >
        {agents.map(agent => {
          const editorTools = agent.type === 'personal'
            ? (paCatalog?.tools ?? []).map(withPaOperationMode)
            : [...internalTools.map(tool => ({ ...tool, is_internal: true })), ...externalTools.map(tool => ({ ...tool, is_internal: false }))];
          const toolCounts = getAgentToolCounts(agent.id);
          const workflowCounts = getAgentWorkflowCounts(agent.id);
          const agentSelectedWorkflows = selectedWorkflows[agent.id] || new Set();

          return (
            <AccordionItem 
              key={agent.id} 
              value={agent.id}
              className="border rounded-lg overflow-hidden"
            >
              <AccordionTrigger className="hover:no-underline px-4 py-3">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      agent.type === 'personal' 
                        ? 'bg-purple-500/20' 
                        : agent.type === 'elevenlabs_sip'
                          ? 'bg-emerald-500/20'
                          : agent.type === 'elevenlabs_webrtc'
                            ? 'bg-cyan-500/20'
                            : agent.type === 'voice' 
                              ? 'bg-green-500/20' 
                              : 'bg-orange-500/20'
                    }`}>
                      {agent.type === 'personal' ? (
                        (() => {
                          const iconMap: Record<string, React.ReactNode> = {
                            bot: <Bot className="w-5 h-5 text-purple-400" />,
                            sparkles: <Sparkles className="w-5 h-5 text-purple-400" />,
                            brain: <Brain className="w-5 h-5 text-purple-400" />,
                            zap: <Zap className="w-5 h-5 text-purple-400" />,
                            shield: <Shield className="w-5 h-5 text-purple-400" />,
                            star: <Star className="w-5 h-5 text-purple-400" />
                          };
                          return iconMap[agent.avatar_icon || 'sparkles'] || <Sparkles className="w-5 h-5 text-purple-400" />;
                        })()
                      ) : agent.type === 'elevenlabs_sip' ? (
                        <Phone className="w-5 h-5 text-emerald-400" />
                      ) : agent.type === 'elevenlabs_webrtc' ? (
                        <Globe className="w-5 h-5 text-cyan-400" />
                      ) : (
                        <Bot className={`w-5 h-5 ${agent.type === 'voice' ? 'text-green-400' : 'text-orange-400'}`} />
                      )}
                    </div>
                    <div className="text-left">
                      <h4 className="font-semibold text-sm">{agent.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {agent.type === 'personal' ? 'Personal AI' 
                          : agent.type === 'elevenlabs_sip' ? 'ElevenLabs SIP' 
                          : agent.type === 'elevenlabs_webrtc' ? 'ElevenLabs WebRTC' 
                          : agent.type === 'voice' ? 'Voice AI' : 'Chat AI'}
                        {agent.description && ` - ${agent.description}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${toolCounts.enabled >= toolCounts.max ? 'border-red-500 text-red-500' : toolCounts.enabled >= toolCounts.max * 0.9 ? 'border-yellow-500 text-yellow-500' : ''}`}
                    >
                      <Wrench className="w-3 h-3 mr-1" />
                      {toolCounts.enabled}/{toolCounts.max}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      <Workflow className="w-3 h-3 mr-1" />
                      {workflowCounts.enabled}/{workflowCounts.total}
                    </Badge>
                    <Badge variant={agent.is_active ? 'default' : 'secondary'} className="text-xs">
                      {agent.is_active ? 'Attivo' : 'Inattivo'}
                    </Badge>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-4 pb-4">
                <div className="flex items-center justify-between mb-4 pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline">
                          <CheckSquare className="w-3 h-3 mr-1" />
                          Azioni Batch
                          <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>Tutti i Tools</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => handleBatchByFilter(agent.id, 'all', undefined, true)}>
                          <Play className="w-4 h-4 mr-2" />
                          Abilita tutti
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBatchByFilter(agent.id, 'all', undefined, false)}>
                          <Pause className="w-4 h-4 mr-2" />
                          Disabilita tutti
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Per Categoria</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => handleBatchByFilter(agent.id, 'category', 'internal', true)}>
                          <Home className="w-4 h-4 mr-2" />
                          Abilita tutti interni
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBatchByFilter(agent.id, 'category', 'internal', false)}>
                          <Home className="w-4 h-4 mr-2" />
                          Disabilita tutti interni
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBatchByFilter(agent.id, 'category', 'external', true)}>
                          <Globe className="w-4 h-4 mr-2" />
                          Abilita tutti esterni
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBatchByFilter(agent.id, 'category', 'external', false)}>
                          <Globe className="w-4 h-4 mr-2" />
                          Disabilita tutti esterni
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Server MCP</DropdownMenuLabel>
                        {Object.entries(externalToolsByServer).map(([serverId, { serverName }]) => (
                          <DropdownMenuItem 
                            key={serverId}
                            onClick={() => handleBatchByFilter(agent.id, 'server', serverId, true)}
                          >
                            <Server className="w-4 h-4 mr-2" />
                            {serverName}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => refreshAgentTools(agent.id)}
                            disabled={refreshingAgent === agent.id}
                          >
                            <RefreshCw className={`w-3 h-3 mr-1 ${refreshingAgent === agent.id ? 'animate-spin' : ''}`} />
                            {refreshingAgent === agent.id ? 'Sync...' : 'Rileggi Tools'}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Forza l'agente a rileggere i tools assegnati</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setCopyTargetAgent(agent.id);
                        setCopyDialogOpen(true);
                      }}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copia da...
                    </Button>
                  </div>
                </div>

                <Accordion type="multiple" defaultValue={['internal', 'external', 'workflows']} className="space-y-2">
                  <AgentToolPermissionsEditor
                    searchQuery={searchQuery}
                    tools={editorTools}
                    permissions={(agentPermissions[agent.id] || []).map(permission => ({
                      server_id: permission.server_id,
                      tool_name: permission.tool_name,
                      enabled: permission.enabled,
                      bulk_allowed: permission.bulk_allowed ?? null,
                    }))}
                    disabled={permissionsLoading || toolsLoading || !!permissionsError || !!serversError || !!toolsError || (agent.type === 'personal' && (paCatalogLoading || !!paCatalogError || !paCatalog?.can_manage)) || updatePermissionMutation.isPending || batchPermissionsMutation.isPending}
                    onChange={changes => {
                      if (changes.length === 1) {
                        const permission = changes[0];
                        if (!permission) return;
                        updatePermissionMutation.mutate({
                          agent_id: agent.id,
                          server_id: permission.server_id,
                          tool_name: permission.tool_name,
                          enabled: permission.enabled,
                          bulk_allowed: agent.type === 'personal' && permission.bulk_allowed !== null ? permission.bulk_allowed : undefined,
                          tool_type: editorTools.find(tool => tool.mcp_server_id === permission.server_id && tool.tool_name === permission.tool_name)?.is_internal ? 'internal' : 'external',
                        });
                      } else if (changes.length > 1) {
                        const first = changes[0];
                        if (!first) return;
                        batchPermissionsMutation.mutate({
                          agent_id: agent.id,
                          tools: changes.map(permission => ({ server_id: permission.server_id, tool_name: permission.tool_name })),
                          enabled: first.enabled,
                        });
                      }
                    }}
                  />

                  <AccordionItem value="workflows" className="border rounded-lg">
                    <AccordionTrigger className="px-3 py-2 hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Workflow className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium">Workflows</span>
                        <Badge variant="secondary" className="text-xs ml-2">
                          {workflows.filter(w => isWorkflowEnabledForAgent(agent.id, w.id)).length}/{workflows.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3">
                      <div className="flex items-center justify-between mb-3 pb-2 border-b">
                        <div className="flex items-center gap-2">
                          {agentSelectedWorkflows.size > 0 && (
                            <>
                              <span className="text-xs text-muted-foreground">
                                {agentSelectedWorkflows.size} selezionati
                              </span>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleBatchWorkflows(agent.id, true)}
                                disabled={batchWorkflowsMutation.isPending}
                              >
                                <Play className="w-3 h-3 mr-1" />
                                Abilita
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleBatchWorkflows(agent.id, false)}
                                disabled={batchWorkflowsMutation.isPending}
                              >
                                <Pause className="w-3 h-3 mr-1" />
                                Disabilita
                              </Button>
                            </>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setCopyWorkflowsTargetAgent(agent.id);
                            setCopyWorkflowsSourceAgent(null);
                            setCopyWorkflowsDialogOpen(true);
                          }}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copia da...
                        </Button>
                      </div>
                      {workflows.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2">
                          Nessun workflow disponibile. Crea un workflow dalla tab "Action Builder".
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {filterWorkflows(workflows).map(wf => (
                            <div 
                              key={wf.id}
                              className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                                agentSelectedWorkflows.has(wf.id) 
                                  ? 'bg-primary/10 border-primary' 
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                            >
                              <Checkbox
                                checked={agentSelectedWorkflows.has(wf.id)}
                                onCheckedChange={() => toggleWorkflowSelection(agent.id, wf.id)}
                              />
                              <div 
                                className="flex items-center gap-2 flex-1 cursor-pointer"
                                onClick={() => {
                                  updateWorkflowBindingMutation.mutate({
                                    agent_id: agent.id,
                                    workflow_id: wf.id,
                                    enabled: !isWorkflowEnabledForAgent(agent.id, wf.id)
                                  });
                                }}
                              >
                                <span className={`w-2 h-2 rounded-full ${isWorkflowEnabledForAgent(agent.id, wf.id) ? 'bg-green-500' : 'bg-gray-400'}`} />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium block truncate">{wf.name}</span>
                                  <span className="text-xs text-muted-foreground block truncate">
                                    {wf.trigger_type} {wf.is_active ? '' : '(bozza)'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copia Configurazione</DialogTitle>
            <DialogDescription>
              Seleziona l'agente da cui copiare la configurazione tools.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {agents.filter(a => a.id !== copyTargetAgent).map(agent => (
              <div 
                key={agent.id}
                onClick={() => setCopySourceAgent(agent.id)}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  copySourceAgent === agent.id 
                    ? 'bg-primary/10 border-primary' 
                    : 'hover:bg-muted/50'
                }`}
              >
                <Bot className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{agent.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {getAgentToolCounts(agent.id).enabled} tools abilitati
                  </p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={() => {
                if (copySourceAgent && copyTargetAgent) {
                  copyPermissionsMutation.mutate({
                    source_agent_id: copySourceAgent,
                    target_agent_id: copyTargetAgent,
                    overwrite: true
                  });
                }
              }}
              disabled={!copySourceAgent || copyPermissionsMutation.isPending}
            >
              {copyPermissionsMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              Copia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={copyWorkflowsDialogOpen} onOpenChange={setCopyWorkflowsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copia Workflows da un altro agente</DialogTitle>
            <DialogDescription>
              Seleziona l'agente da cui copiare i bindings workflows verso "{agents.find(a => a.id === copyWorkflowsTargetAgent)?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {agents
              .filter(agent => agent.id !== copyWorkflowsTargetAgent)
              .map(agent => (
              <div
                key={agent.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  copyWorkflowsSourceAgent === agent.id 
                    ? 'bg-primary/10 border-primary' 
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => setCopyWorkflowsSourceAgent(agent.id)}
              >
                <Bot className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{agent.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {getAgentWorkflowCounts(agent.id).enabled} workflows abilitati
                  </p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyWorkflowsDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={() => {
                if (copyWorkflowsSourceAgent && copyWorkflowsTargetAgent) {
                  copyWorkflowsMutation.mutate({
                    source_agent_id: copyWorkflowsSourceAgent,
                    target_agent_id: copyWorkflowsTargetAgent,
                    overwrite: true
                  });
                }
              }}
              disabled={!copyWorkflowsSourceAgent || copyWorkflowsMutation.isPending}
            >
              {copyWorkflowsMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              Copia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
